// frontend/src/components/common/SessionManager.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from './Button';

const SessionManager = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxSessions, setMaxSessions] = useState(4);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sessions');
      setSessions(response.data.sessions);
      setMaxSessions(response.data.maxSessions);
    } catch (error) {
      console.error('Erreur récupération sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutSession = async (sessionId) => {
    if (!window.confirm('Voulez-vous vraiment déconnecter cet appareil ?')) {
      return;
    }

    try {
      await api.delete(`/sessions/${sessionId}`);
      fetchSessions();
    } catch (error) {
      console.error('Erreur déconnexion session:', error);
      alert('Erreur lors de la déconnexion');
    }
  };

  const handleLogoutOthers = async () => {
    if (!window.confirm('Voulez-vous déconnecter tous les autres appareils ?')) {
      return;
    }

    try {
      const response = await api.post('/sessions/logout-others');
      alert(response.data.message);
      fetchSessions();
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      alert('Erreur lors de la déconnexion');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeviceIcon = (device) => {
    const icons = {
      mobile: '📱',
      tablet: '📱',
      desktop: '💻',
    };
    return icons[device] || '💻';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Appareils connectés</h2>
          <p className="text-gray-600 mt-1">
            {sessions.length} / {maxSessions} appareils actifs
          </p>
        </div>

        {sessions.length > 1 && (
          <Button
            variant="danger"
            onClick={handleLogoutOthers}
          >
            Déconnecter les autres
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {sessions.map((session) => (
          <div
            key={session.id}
            className={`border rounded-lg p-4 ${
              session.isCurrent
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">{session.deviceIcon}</div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-800">
                      {session.deviceInfo.deviceName}
                    </h3>
                    {session.isCurrent && (
                      <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
                        Cet appareil
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <p>
                      <span className="font-medium">Navigateur:</span>{' '}
                      {session.deviceInfo.browser}
                    </p>
                    <p>
                      <span className="font-medium">Système:</span>{' '}
                      {session.deviceInfo.os}
                    </p>
                    <p>
                      <span className="font-medium">Adresse IP:</span>{' '}
                      {session.ipAddress}
                    </p>
                    <p>
                      <span className="font-medium">Dernière activité:</span>{' '}
                      {formatDate(session.lastActivity)}
                    </p>
                  </div>
                </div>
              </div>

              {!session.isCurrent && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleLogoutSession(session.id)}
                >
                  Déconnecter
                </Button>
              )}
            </div>
          </div>
        ))}

        {sessions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucune session active
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
      <p className="text-sm text-blue-800">
        <strong>ℹ️ Information:</strong> Vous pouvez vous connecter sur un maximum de {maxSessions} appareils simultanément.
        Les sessions restent actives pendant <strong>30 jours</strong> sans activité.
        La session la plus ancienne sera automatiquement déconnectée si vous dépassez cette limite.
    </p>
      </div>
    </div>
  );
};

export default SessionManager;